import type { IndexerSearchDocsQuery } from '@affine/graphql';

import {
  type DocsSearchAggregateItem,
  markFirstQueryMatch,
} from './docs-search-aggregate';
import { getDocsSearchLocationIdentity } from './source-navigation';

export function mapRemoteSearchDocsForQuickSearch(
  docs: IndexerSearchDocsQuery['workspace']['searchDocs'],
  query: string
): DocsSearchAggregateItem[] {
  return docs.flatMap((doc, index) => {
    const databaseTitle = doc.databaseTitle || undefined;
    const databaseRowLabel = doc.rowLabel || undefined;
    const databaseColumnName = doc.columnName || undefined;
    const highlight = doc.highlight || undefined;
    const searchVisibility =
      doc.visibility || doc.technicalSourceKind
        ? {
            visibility: doc.visibility || undefined,
            technicalSourceKind: doc.technicalSourceKind || undefined,
          }
        : {};
    const blockContent = doc.databaseId
      ? formatDatabaseSearchSnippet(
          {
            databaseTitle,
            databaseRowLabel,
            highlight,
          },
          query
        )
      : markFirstQueryMatch(highlight, query);
    if (doc.databaseId && !blockContent) return [];

    return [
      {
        docId: doc.docId,
        title: doc.title,
        blockId: doc.databaseId || doc.blockId || undefined,
        blockContent,
        sourceType: doc.databaseId ? 'database' : doc.blockId ? 'text' : 'doc',
        databaseId: doc.databaseId || undefined,
        databaseRowId: doc.rowId || undefined,
        databaseRowShardDocId: doc.rowShardDocId || undefined,
        databaseColumnId: doc.columnId || undefined,
        databaseTitle,
        databaseRowLabel,
        databaseColumnName,
        ...searchVisibility,
        // GraphQL currently exposes sorted searchDocs rows without raw score.
        // Preserve backend order while keeping these rows visible in QuickSearch.
        score: 10_000 - index,
      },
    ];
  });
}

function formatDatabaseSearchSnippet(
  input: {
    databaseTitle?: string;
    databaseRowLabel?: string;
    highlight?: string;
  },
  query: string
) {
  const valueMatches = hasQueryMatch(input.highlight, query);
  const rowLabelMatches = hasQueryMatch(input.databaseRowLabel, query);
  if (!valueMatches && !rowLabelMatches) return undefined;

  const rowLabel = rowLabelMatches
    ? markFirstQueryMatch(input.databaseRowLabel, query)
    : input.databaseRowLabel;
  const context = [input.databaseTitle, rowLabel].filter(Boolean).join(' · ');
  if (!valueMatches) return context;

  const value = markFirstQueryMatch(input.highlight, query);
  if (context && value) return `${context}: ${value}`;
  return value || context;
}

function hasQueryMatch(content: string | undefined, query: string) {
  if (!content) return false;
  if (content.includes('<b>')) return true;
  const normalizedQuery = query.trim().toLowerCase();
  return (
    normalizedQuery.length > 0 &&
    content.toLowerCase().includes(normalizedQuery)
  );
}

export function mergeRemoteDocsSearchResults(
  aggregateResults: DocsSearchAggregateItem[],
  searchDocsResults: DocsSearchAggregateItem[]
): DocsSearchAggregateItem[] {
  const docIdsWithGranularAggregateHits = new Set(
    aggregateResults
      .filter(
        result =>
          result.sourceType !== 'doc' ||
          Boolean(result.blockId) ||
          Boolean(result.elementId) ||
          Boolean(result.databaseId)
      )
      .map(result => result.docId)
  );
  const seen = new Set<string>();
  const merged: DocsSearchAggregateItem[] = [];

  const filteredSearchDocsResults = searchDocsResults.filter(result => {
    const isGenericDocHit =
      result.sourceType === 'doc' &&
      !result.blockId &&
      !result.elementId &&
      !result.databaseId;
    return !(
      isGenericDocHit && docIdsWithGranularAggregateHits.has(result.docId)
    );
  });

  // searchDocs carries database provenance, but for ordinary block hits it
  // does not expose the indexed flavour. Prefer those database rows first,
  // then the flavour-aware aggregate hits, and use remaining searchDocs rows
  // only as a fallback. This keeps the correct image/attachment/embed source
  // kind when both endpoints return the same physical block.
  const databaseSearchDocsResults = filteredSearchDocsResults.filter(
    result => result.sourceType === 'database'
  );
  const fallbackSearchDocsResults = filteredSearchDocsResults.filter(
    result => result.sourceType !== 'database'
  );

  for (const result of [
    ...databaseSearchDocsResults,
    ...aggregateResults,
    ...fallbackSearchDocsResults,
  ]) {
    const key = getDocsSearchLocationIdentity(result);
    if (seen.has(key)) continue;
    seen.add(key);
    merged.push(result);
  }

  return merged.sort((a, b) => b.score - a.score);
}
